import {
  LOWER_DISPLAY_ORDER,
  TOOTH_POSITIONS_BY_NUMBER,
  UPPER_DISPLAY_ORDER,
  toothPosition,
  viewerSideLabel,
  type ToothPosition,
} from '@toothsomatics/tooth-model';
import { useCallback, type KeyboardEvent } from 'react';

import { hasContent, type ChartEntries } from './types.js';
import './chart.css';

/**
 * The dentition, drawn as an arch.
 *
 * The geometry is not decoration. `packages/tooth-model` walks an ellipse by arc length and gives
 * each position a share proportional to its real relative width, so molars sit wider and further
 * back and the whole thing reads as a mouth rather than two rows of equal boxes. Everything here
 * consumes those numbers; none of it recomputes them.
 *
 * Orientation is the client's own — "your right" appears on the viewer's left, as in a mirror. The
 * imaging convention that labels the sides from the viewer's seat is not used and its vocabulary is
 * forbidden (CLAUDE.md, law 1).
 *
 * One tab stop, then arrow keys. Thirty-two tab stops in the middle of a form is a keyboard trap in
 * everything but name, so the arch is a single composite widget: roving tabindex, arrows to move,
 * Enter or Space to open the panel.
 */

/**
 * Padding around the extreme tooth centres so a rotated crown never clips.
 *
 * The furthest a crown reaches from its own centre is half its diagonal — roughly 42 units for the
 * widest molar — and rotation can point that in any direction.
 */
const VIEWBOX_PADDING = 48;

/**
 * How much of its allotted arc a crown actually fills. The remainder is the gap between
 * neighbours, which is what stops the arch reading as one continuous band.
 *
 * `position.width` is an **arc length in coordinate units** (41–70 here), not a relative multiplier.
 * Reading it as a multiplier is what produced crowns a thousand units wide.
 */
const CROWN_FILL = 0.82;

interface CrownSpec {
  /** Crown height in the same units as the arch coordinates. */
  readonly height: number;
  /** Bumps along the biting edge. 1 is a flat chisel; 0 means a single point. */
  readonly cusps: number;
  /** How far the corners sit below the biting edge, as a fraction of height. */
  readonly relief: number;
  /** Width at the gum, as a fraction of the widest point. */
  readonly waist: number;
}

/**
 * Silhouettes, by type. Not decoration: an incisor that looks like a molar makes the chart harder
 * to read than numbers alone would.
 */
const CROWN: Readonly<Record<ToothPosition['type'], CrownSpec>> = {
  incisor: { height: 56, cusps: 1, relief: 0, waist: 0.74 },
  canine: { height: 60, cusps: 0, relief: 0.18, waist: 0.72 },
  premolar: { height: 50, cusps: 2, relief: 0.1, waist: 0.8 },
  molar: { height: 46, cusps: 4, relief: 0.08, waist: 0.88 },
};

const ALL_IN_DISPLAY_ORDER: readonly number[] = [...UPPER_DISPLAY_ORDER, ...LOWER_DISPLAY_ORDER];

function viewBox(): string {
  const positions = [...TOOTH_POSITIONS_BY_NUMBER.values()];
  const xs = positions.map((position) => position.x);
  const ys = positions.map((position) => position.y);
  const minX = Math.min(...xs) - VIEWBOX_PADDING;
  const minY = Math.min(...ys) - VIEWBOX_PADDING;
  const maxX = Math.max(...xs) + VIEWBOX_PADDING;
  const maxY = Math.max(...ys) + VIEWBOX_PADDING;
  return `${String(minX)} ${String(minY)} ${String(maxX - minX)} ${String(maxY - minY)}`;
}

const VIEW_BOX = viewBox();

/**
 * The biting edge, drawn left to right between the two corners.
 *
 * A canine comes to a point, an incisor is a flat chisel, and the back teeth are scalloped — each
 * cusp is one quadratic bulging past the corner line, with the fissure between two cusps sitting
 * back at corner height.
 */
function bitingEdge(shoulder: number, cornerY: number, edgeY: number, cusps: number): string {
  if (cusps === 0) {
    return `Q ${-shoulder * 0.42} ${edgeY} 0 ${edgeY} Q ${shoulder * 0.42} ${edgeY} ${shoulder} ${cornerY}`;
  }
  if (cusps === 1) return `L ${shoulder} ${cornerY}`;

  const step = (shoulder * 2) / cusps;
  return Array.from({ length: cusps }, (_unused, index) => {
    const from = -shoulder + step * index;
    return `Q ${from + step / 2} ${edgeY} ${from + step} ${cornerY}`;
  }).join(' ');
}

/**
 * One crown, in its own local space: the gum at negative y, the biting edge at positive y.
 *
 * There is no per-arch flip here. `position.angle` already points each crown away from the centre
 * of its own ellipse — the upper arch spans −66°…66° and the lower is the same plus 180° — so the
 * lower teeth bite upward on their own. Flipping as well would turn them back over.
 */
function crownPath(position: ToothPosition): string {
  const spec = CROWN[position.type];
  const halfWidth = (position.width * CROWN_FILL) / 2;
  const halfHeight = spec.height / 2;

  const gum = -halfHeight;
  const edge = halfHeight;
  const corner = edge - spec.relief * spec.height;
  const waist = halfWidth * spec.waist;
  const shoulder = halfWidth * 0.96;

  return [
    `M ${-waist} ${gum}`,
    // Down the left side, bulging to the widest point about a third from the edge.
    `C ${-halfWidth} ${gum + spec.height * 0.32} ${-halfWidth} ${edge - spec.height * 0.26} ${-shoulder} ${corner}`,
    bitingEdge(shoulder, corner, edge, spec.cusps),
    `C ${halfWidth} ${edge - spec.height * 0.26} ${halfWidth} ${gum + spec.height * 0.32} ${waist} ${gum}`,
    // The gum line lifts very slightly rather than cutting straight across.
    `Q 0 ${gum - spec.height * 0.08} ${-waist} ${gum}`,
    'Z',
  ].join(' ');
}

export interface ToothChartProps {
  readonly entries: ChartEntries;
  readonly selected: number;
  readonly onSelect: (toothNumber: number) => void;
}

export function ToothChart({ entries, selected, onSelect }: ToothChartProps) {
  const move = useCallback(
    (event: KeyboardEvent<SVGGElement>) => {
      const index = ALL_IN_DISPLAY_ORDER.indexOf(selected);
      if (index < 0) return;

      const step = (delta: number) => {
        event.preventDefault();
        const next =
          ALL_IN_DISPLAY_ORDER[
            (index + delta + ALL_IN_DISPLAY_ORDER.length) % ALL_IN_DISPLAY_ORDER.length
          ];
        if (next !== undefined) onSelect(next);
      };

      switch (event.key) {
        case 'ArrowRight':
          return step(1);
        case 'ArrowLeft':
          return step(-1);
        // Up and down cross between the arches, keeping roughly the same place along the arch.
        case 'ArrowDown':
        case 'ArrowUp':
          return step(UPPER_DISPLAY_ORDER.length);
        case 'Home':
          event.preventDefault();
          return onSelect(ALL_IN_DISPLAY_ORDER[0] ?? selected);
        case 'End':
          event.preventDefault();
          return onSelect(ALL_IN_DISPLAY_ORDER[ALL_IN_DISPLAY_ORDER.length - 1] ?? selected);
        default:
          return undefined;
      }
    },
    [onSelect, selected],
  );

  const recorded = ALL_IN_DISPLAY_ORDER.filter((number) => hasContent(entries[number])).length;

  return (
    <figure className="chart">
      <figcaption className="chart__orientation">
        <span>{viewerSideLabel('right')}</span>
        <span className="chart__count">
          {recorded === 0
            ? 'Nothing recorded yet'
            : `${String(recorded)} of ${String(ALL_IN_DISPLAY_ORDER.length)} positions recorded`}
        </span>
        <span>{viewerSideLabel('left')}</span>
      </figcaption>

      <p className="sr-only" id="chart-help">
        Use the arrow keys to move between positions. Press Enter or Space to open the panel for the
        position you are on.
      </p>

      <svg
        className="chart__svg"
        viewBox={VIEW_BOX}
        role="group"
        aria-label="Your dentition, upper and lower"
        aria-describedby="chart-help"
      >
        {ALL_IN_DISPLAY_ORDER.map((toothNumber, index) => {
          const position = toothPosition(toothNumber);
          const isSelected = toothNumber === selected;
          const isRecorded = hasContent(entries[toothNumber]);

          return (
            <g
              key={toothNumber}
              className={[
                'tooth',
                isSelected ? 'tooth--selected' : '',
                isRecorded ? 'tooth--recorded' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              transform={`translate(${String(position.x)} ${String(position.y)}) rotate(${String(position.angle)})`}
              /* Staggered so the arch settles into place rather than appearing all at once.
                 Zeroed with every other duration under prefers-reduced-motion. */
              style={{ '--tooth-index': index } as React.CSSProperties}
              role="button"
              tabIndex={isSelected ? 0 : -1}
              aria-pressed={isSelected}
              aria-label={`${position.accessibleName}${isRecorded ? ', has notes' : ''}`}
              onClick={() => onSelect(toothNumber)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(toothNumber);
                  return;
                }
                move(event);
              }}
            >
              <path className="tooth__crown" d={crownPath(position)} />
              {/* Counter-rotated so the number stays upright whatever the crown's angle. */}
              <text
                className="tooth__number"
                transform={`rotate(${String(-position.angle)})`}
                dy="0.36em"
              >
                {toothNumber}
              </text>
            </g>
          );
        })}
      </svg>

      <ul className="chart__legend">
        <li>Molars</li>
        <li>Premolars</li>
        <li>Canines</li>
        <li>Incisors</li>
        <li className="chart__legend-hint">Choose any position to record what you know about it</li>
      </ul>
    </figure>
  );
}
